/**
 * 経由点 (必ず通過する点) を「実際に描かれた経路の上」へ寄せる。
 *
 * 経由点は Mapbox Directions に渡す時点で**歩行ネットワーク上の最寄り地点へ
 * 上限なしに吸着**される。歩ける道が無い場所に置くと、経路は十数m〜数十m 離れた
 * ところを通り、ピンだけが線から外れて「経由していない」ように見える
 * (実測: プライムアーバン勝どきで 15.5m。吸着先は経路の出発点のすぐ隣だった)。
 *
 * 吸着の距離を縛っても直らない — 実測では radiuses=30m で結果は変わらず、10m では
 * 「近くに歩ける道が無い」で経路自体が取れなくなる。歩ける道がそこに無いのが理由。
 *
 * そこで取得後に**ピンを経路上へ移す**。併せて動いた距離を返し、大きく動いたときは
 * 呼び出し側が「道が無いので寄せた」と知らせる。
 */
import nearestPointOnLine from '@turf/nearest-point-on-line';
import { lineString, point as turfPoint } from '@turf/helpers';
import type { LngLat } from '@/types/property';
import { haversineMeters } from './distance';
import { splitEntranceWaypoint } from './route-postprocess';

export interface SnappedWaypoint {
  /** 経路上の最寄り点。 */
  point: LngLat;
  /** 元の位置からの移動距離 (m)。 */
  movedM: number;
}

/**
 * 経由点群を経路 (座標列) 上の最寄り点へ寄せる。
 * 経路が2点未満なら、寄せずにそのまま返す (移動距離 0)。
 */
export function snapWaypointsToRoute(
  waypoints: LngLat[],
  route: Array<[number, number]>,
): SnappedWaypoint[] {
  if (route.length < 2) {
    return waypoints.map((w) => ({ point: { lng: w.lng, lat: w.lat }, movedM: 0 }));
  }
  const line = lineString(route.map(([lng, lat]) => [lng, lat]));
  return waypoints.map((w) => {
    try {
      const near = nearestPointOnLine(line, turfPoint([w.lng, w.lat]));
      const c = near.geometry.coordinates;
      if (c.length < 2 || !Number.isFinite(c[0]) || !Number.isFinite(c[1])) {
        return { point: { lng: w.lng, lat: w.lat }, movedM: 0 };
      }
      const point = { lng: c[0]!, lat: c[1]! };
      return { point, movedM: haversineMeters(w, point) };
    } catch {
      // turf が扱えない座標 (重複点のみの経路等) は動かさない
      return { point: { lng: w.lng, lat: w.lat }, movedM: 0 };
    }
  });
}

/** 経由点がこれ以上動いたら、寄せた理由をユーザーに知らせる (m)。 */
export const WAYPOINT_SNAP_NOTICE_M = 15;

/**
 * 経路を確定したときの経由点の寄せ (v1 避難マップ / v2 災害時避難先 共通)。
 *
 * 通過点は経路上へ寄せ、大きく動いたら知らせる文言を返す。避難先の内側に置いた最後の
 * 経由点は**入口** (経路の終点, splitEntranceWaypoint) で通過点ではないので動かさない —
 * 線上へ寄せると縁の外へ出て、次の取得で入口として扱われなくなる。
 */
export function snapWaypointsForRoute(
  waypoints: LngLat[],
  route: Array<[number, number]>,
  destRing: Array<[number, number]> | null,
): { waypoints: LngLat[]; notice: string | null } {
  const { entrance, vias } = splitEntranceWaypoint(waypoints, destRing);
  const snapped = snapWaypointsToRoute(vias, route);
  const far = snapped.filter((x) => x.movedM >= WAYPOINT_SNAP_NOTICE_M);
  return {
    waypoints: [...snapped.map((x) => x.point), ...(entrance ? [entrance] : [])],
    notice: far.length
      ? `経由点${far.length}個の位置に歩ける道が無いため、経路上 (最大 ${Math.round(
          Math.max(...far.map((x) => x.movedM)),
        )}m 先) へ寄せました。意図した道と違うときは置き直してください。`
      : null,
  };
}
